import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Measures a content element's height and returns an animated outer height value.
 *
 * Usage:
 *   const { containerRef, contentRef, heightStyle } = useAnimatedHeight(key);
 *
 * - Wrap the outer box with `ref={containerRef}` and `sx={heightStyle}`.
 * - Wrap the inner content with `ref={contentRef}`.
 * - `key` should change whenever the content changes (e.g. the current step name).
 *   When it changes, the hook measures the new content height and transitions to it.
 *
 * A ResizeObserver is also attached to `contentRef` so that any internal size
 * changes (e.g. a collapsible section expanding inside the content) are picked
 * up continuously — not just on `key` changes.
 */
export function useAnimatedHeight<K>(key: K, durationMs = 260) {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | 'auto'>('auto');

    const measure = useCallback(() => {
        if (contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
        }
    }, []);

    // Re-measure whenever the key (active step) changes.
    // useLayoutEffect fires synchronously after DOM paint so we get the right
    // scrollHeight before the browser has had a chance to repaint.
    useLayoutEffect(() => {
        measure();
    }, [key, measure]);

    // Also re-measure continuously whenever the content resizes — this handles
    // internal size changes such as a Collapse animation expanding inside the content.
    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [measure]);

    const heightStyle = {
        height: height === 'auto' ? 'auto' : `${height}px`,
        overflow: 'hidden',
        transition: `height ${durationMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    } as const;

    return { containerRef, contentRef, heightStyle };
}
